import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import api from '../../../utils/api';
import { toast } from 'sonner';

const scannerDivId = 'checkout-barcode-scanner';

const BarcodeScanModal = ({ isOpen, onClose, onBarcodeScanned }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [manualBarcode, setManualBarcode] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('Idle');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [productData, setProductData] = useState(null);
  const scannerRef = useRef(null);
  const hasDecodedRef = useRef(false);
  const errorLogCountRef = useRef(0);

  useEffect(() => {
    console.log('[CheckoutScanner] mounted');
    return () => {
      console.log('[CheckoutScanner] unmount');
      if (scannerRef.current) {
        console.log('[CheckoutScanner] Unmount -> stopping scanner');
        scannerRef.current.stop().catch(() => {});
        try { scannerRef.current.clear(); } catch {}
        scannerRef.current = null;
      }
    };
  }, []);

  const startScanner = async () => {
    console.log('[CheckoutScanner] startScanner called');
    setStatus('Starting');
    setError('');
    setScanResult(null);
    hasDecodedRef.current = false;
    errorLogCountRef.current = 0;

    if (scannerRef.current) {
      console.log('[CheckoutScanner] Scanner already running');
      setStatus('Running');
      return;
    }

    try {
      setIsScanning(true);
      let cameras = [];
      try {
        cameras = await Html5Qrcode.getCameras();
        console.log('[CheckoutScanner] Cameras discovered:', cameras);
      } catch (camErr) {
        console.error('[CheckoutScanner] getCameras failed:', camErr);
        setStatus('Camera discovery failed');
        setIsScanning(false);
        return;
      }
      if (!cameras || cameras.length === 0) {
        setError('No camera found. Try manual entry.');
        setStatus('No camera');
        setIsScanning(false);
        return;
      }
      const chosen = (cameras.find(c => /back|rear|environment/i.test(c.label)) || cameras[0]);
      const deviceId = chosen.id;
      console.log('[CheckoutScanner] Using camera:', chosen);

      const container = document.getElementById(scannerDivId);
      if (!container) {
        console.warn('[CheckoutScanner] Scanner container not found');
        setError('Scanner container not found');
        setStatus('Container missing');
        setIsScanning(false);
        return;
      }

      const scanner = new Html5Qrcode(scannerDivId, /* verbose= */ false);
      scannerRef.current = scanner;
      console.log('[CheckoutScanner] Starting scanner...');
      await scanner.start(
        { deviceId: { exact: deviceId } },
        {
          fps: 15,
          qrbox: 260,
          aspectRatio: 1.7778,
          formatsToSupport: [
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E,
            Html5QrcodeSupportedFormats.CODE_39,
            Html5QrcodeSupportedFormats.CODE_93,
            Html5QrcodeSupportedFormats.CODE_128,
          ],
          experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        },
        (decodedText) => {
          if (hasDecodedRef.current) {
            console.log('[CheckoutScanner] Already decoded, ignoring:', decodedText);
            return;
          }
          hasDecodedRef.current = true;
          console.log('[CheckoutScanner] Successfully decoded barcode:', decodedText);
          setStatus('Decoded');
          setScanResult(decodedText);
          toast.success(`Barcode scanned: ${decodedText}`);
          stopScanner();
          // Automatically lookup the barcode
          lookupBarcode(decodedText);
        },
        (errorMessage) => {
          errorLogCountRef.current++;
          if (errorLogCountRef.current <= 3) {
            console.log('[CheckoutScanner] Scan error (suppressed):', errorMessage);
          }
        }
      );
      setStatus('Running');
      console.log('[CheckoutScanner] Scanner started successfully');
    } catch (e) {
      console.error('[CheckoutScanner] startScanner error:', e);
      setError('Failed to start camera. Check permissions.');
      setStatus('Error');
      setIsScanning(false);
      if (scannerRef.current) {
        try { scannerRef.current.clear(); } catch {}
        scannerRef.current = null;
      }
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      console.log('[CheckoutScanner] Stopping scanner');
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch (e) {
        console.warn('[CheckoutScanner] Error stopping/clearing scanner', e);
      }
      scannerRef.current = null;
    }
    setIsScanning(false);
    setStatus('Stopped');
  };

  const lookupBarcode = async (barcode) => {
    if (!barcode || !barcode.trim()) return;
    
    const code = barcode.trim();
    console.log('[CheckoutScanner] lookupBarcode:', code);
    
    try {
      setIsLookingUp(true);
      setError('');
      
      // Try to find product in our inventory
      const { data } = await api.get(`/barcode/${encodeURIComponent(code)}`);
      console.log('[CheckoutScanner] Barcode lookup response:', data);
      
      if (data && data.id) {
        // Check if it's from our inventory (firestore) or has inventory data
        if (data.source === 'firestore' || (data.quantity !== undefined && data.quantity !== null)) {
          // Product found in our inventory
          setProductData(data);
          console.log('[CheckoutScanner] Product found in inventory:', data);
          
          // Add to cart database
          try {
            // Add to cart database first
            await api.post('/cart', {
              product: data,
              quantity: 1
            });
            
            // Then call the callback for UI update
            onBarcodeScanned(code, data);
            toast.success(`Added: ${data.name}`, {
              description: `${data.quantity} in stock`
            });
            
            // Close modal after a short delay to ensure parent state updates
            setTimeout(() => {
              handleClose();
            }, 300);
          } catch (err) {
            console.error('[CheckoutScanner] Error adding item to cart:', err);
            toast.error('Error adding item to cart', {
              description: err.message || "Please try again"
            });
          }
        } else if (data.source === 'openfoodfacts') {
          // Product found via external API but not in inventory
          setProductData(data);
          toast.warning('Product found but not in inventory', {
            description: "This product needs to be added to your catalog first"
          });
          setError('Product not in inventory. Add it to catalog first.');
        } else {
          // Product data exists but unclear source
          console.warn('[CheckoutScanner] Product found but source unclear:', data);
          setProductData(data);
          
          try {
            onBarcodeScanned(code, data);
            toast.success(`Added: ${data.name}`, {
              description: `${data.quantity} in stock`
            });
            
            setTimeout(() => {
              handleClose();
            }, 300);
          } catch (err) {
            console.error('[CheckoutScanner] Error in onBarcodeScanned callback:', err);
            toast.error('Error adding item to cart', {
              description: err.message || "Please try again"
            });
          }
        }
      } else {
        // No product found
        console.log('[CheckoutScanner] No product data or missing id');
        setError('Product not found in inventory.');
        toast.error('Product not found', {
          description: "This product needs to be added to your catalog first",
          action: {
            label: "Add Product",
            onClick: () => {
              window.location.href = `/product-catalog?addProduct=true&barcode=${encodeURIComponent(code)}`;
            }
          }
        });
      }
    } catch (err) {
      console.error('[CheckoutScanner] Lookup error:', err);
      console.error('[CheckoutScanner] Error details:', err.response?.data);
      setError('Error looking up product. Try again.');
      toast.error('Error looking up product', {
        description: err.response?.data?.message || "Please try again or add the product manually",
        action: {
          label: "Try Again",
          onClick: () => lookupBarcode(code)
        }
      });
    } finally {
      setIsLookingUp(false);
    }
  };

  const handleBarcodeSubmit = (barcode) => {
    console.log('[CheckoutScanner] handleBarcodeSubmit:', barcode);
    if (barcode && barcode.trim()) {
      lookupBarcode(barcode.trim());
    }
  };

  const handleManualSubmit = (e) => {
    e?.preventDefault();
    handleBarcodeSubmit(manualBarcode);
  };

  const handleClose = () => {
    console.log('[CheckoutScanner] Closing modal');
    stopScanner();
    setManualBarcode('');
    setScanResult(null);
    setError('');
    setProductData(null);
    onClose();
  };

  const handleUseScanResult = () => {
    if (scanResult) handleBarcodeSubmit(scanResult);
  };

  const handleScanAnother = () => {
    setScanResult(null);
    setError('');
    setProductData(null);
    startScanner();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-0 sm:p-4">
      <div className="bg-card w-full max-w-md rounded-lg shadow-xl overflow-hidden m-0 sm:m-4">
        <div className="max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
            <h3 className="text-lg font-semibold text-foreground">Scan Product Barcode</h3>
            <Button variant="ghost" size="icon" onClick={handleClose} disabled={isScanning}>
              <Icon name="X" size={20} />
            </Button>
          </div>

          <div className="space-y-6 p-4">
            {/* Camera Scan Section */}
            <div className="text-center">
              <div className="w-full h-48 bg-muted border-2 border-dashed border-border rounded-lg flex items-center justify-center mb-4 overflow-hidden">
                {isScanning ? (
                  <div id={scannerDivId} className="w-full h-full" />
                ) : scanResult ? (
                  <div className="text-center">
                    <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-3" />
                    <p className="text-sm font-medium text-foreground">Barcode Detected!</p>
                    <p className="text-lg font-data text-primary mt-1">{scanResult}</p>
                    {isLookingUp && (
                      <p className="text-xs text-muted-foreground mt-2">Looking up product...</p>
                    )}
                  </div>
                ) : (
                  <div className="text-center px-4">
                    <Icon name="Scan" size={48} className="text-muted-foreground mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">Click "Start Scanning" to begin</p>
                    {error && <p className="text-xs text-destructive mt-2">{error}</p>}
                    <p className="text-[10px] text-muted-foreground mt-1">Status: {status}</p>
                  </div>
                )}
              </div>

              {scanResult && !isLookingUp ? (
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    onClick={handleScanAnother} 
                    iconName="RotateCcw" 
                    iconPosition="left" 
                    iconSize={16}
                    className="flex-1"
                  >
                    Scan Another
                  </Button>
                  {!error && (
                    <Button 
                      variant="default" 
                      onClick={handleClose} 
                      iconName="Check" 
                      iconPosition="left" 
                      iconSize={16}
                      className="flex-1"
                    >
                      Done
                    </Button>
                  )}
                </div>
              ) : (
                <Button
                  variant="default"
                  onClick={() => { console.log('[CheckoutScanner] Start button clicked'); startScanner(); }}
                  disabled={isScanning || isLookingUp}
                  loading={isScanning || isLookingUp}
                  iconName="Scan"
                  iconPosition="left"
                  iconSize={16}
                  className="w-full"
                >
                  {isScanning ? 'Scanning...' : isLookingUp ? 'Looking up...' : 'Start Scanning'}
                </Button>
              )}
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-card text-muted-foreground">or</span>
              </div>
            </div>

            {/* Manual Entry Section */}
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <Input
                label="Enter Barcode Manually"
                type="text"
                placeholder="Type or paste barcode number"
                value={manualBarcode}
                onChange={(e) => setManualBarcode(e?.target?.value)}
                description="Enter the barcode number if camera scanning is not available"
              />
              <div className="flex items-center space-x-3">
                <Button type="button" variant="ghost" onClick={handleClose} className="flex-1">
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  variant="default" 
                  disabled={!manualBarcode?.trim() || isLookingUp} 
                  loading={isLookingUp}
                  iconName="Search" 
                  iconPosition="left" 
                  iconSize={16} 
                  className="flex-1"
                >
                  {isLookingUp ? 'Looking up...' : 'Add to Cart'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarcodeScanModal;
